import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import GlassmorphismTokens from '../../styles/GlassmorphismTokens';
import SwipeCard from './SwipeCard';
import SwipeResultReveal from './SwipeResultReveal';
import SWIPE_CARDS, { computeDNAFromSwipes, computePsychoFromSwipes } from '../../data/swipeCardData';
import { deriveTravelSoul } from '../../data/psychographicData';
import haptic from '../../utils/haptics';

const Container = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${GlassmorphismTokens.dark.overlay};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
`;

const Header = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  z-index: 10;
`;

const SkipButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  cursor: pointer;
  padding: 8px 12px;

  &:hover {
    color: rgba(255, 255, 255, 0.8);
  }
`;

const ProgressText = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
`;

const DeckArea = styled.div`
  position: relative;
  width: 300px;
  height: 420px;

  @media (max-width: 480px) {
    width: 280px;
    height: 400px;
  }
`;

const ProgressDots = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 32px;
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props =>
    props.$state === 'liked' ? '#22c55e' :
    props.$state === 'passed' ? '#ef4444' :
    props.$state === 'current' ? 'white' :
    'rgba(255, 255, 255, 0.2)'};
  transition: background 0.3s;
`;

const Hint = styled(motion.p)`
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
  margin-top: 16px;
  text-align: center;
`;

const SwipeOnboarding = ({ onComplete, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [decisions, setDecisions] = useState([]); // { card, liked: boolean }[]
  const [result, setResult] = useState(null);
  const [, setExitCards] = useState([]); // track exiting card animations

  const handleSwipe = useCallback((direction) => {
    const card = SWIPE_CARDS[currentIndex];
    const liked = direction === 'right';

    haptic[liked ? 'light' : 'medium']();

    setDecisions(prev => [...prev, { card, liked }]);
    setExitCards(prev => [...prev, currentIndex]);

    const nextIndex = currentIndex + 1;

    if (nextIndex >= SWIPE_CARDS.length) {
      // All cards swiped — compute results
      const allDecisions = [...decisions, { card, liked }];
      const likedCards = allDecisions.filter(d => d.liked).map(d => d.card);

      const dna = computeDNAFromSwipes(likedCards);
      const psychographic = computePsychoFromSwipes(likedCards);
      const travelSoul = deriveTravelSoul(psychographic);

      // Map DNA values from 0-100 to 0-1 for compatibility with existing computeDNA format
      const normalizedDimensions = {
        culture: (dna.cultural || 0) / 100,
        adventure: (dna.adventure || 0) / 100,
        relaxation: (dna.relaxation || 0) / 100,
        food: (dna.culinary || 0) / 100,
        nature: (dna.nature || 0) / 100,
        social: (dna.luxury || 0) / 100,
      };

      // Find personality label using existing computeDNA logic
      const PERSONALITY_LABELS = [
        { condition: (d) => d.culture >= 0.6 && d.adventure >= 0.5, label: 'The Cultural Explorer' },
        { condition: (d) => d.adventure >= 0.7 && d.nature >= 0.6, label: 'The Trailblazer' },
        { condition: (d) => d.food >= 0.7 && d.culture >= 0.5, label: 'The Adventurous Foodie' },
        { condition: (d) => d.relaxation >= 0.6 && d.nature >= 0.5, label: 'The Zen Seeker' },
        { condition: (d) => d.social >= 0.7, label: 'The Social Nomad' },
        { condition: (d) => d.culture >= 0.6, label: 'The History Buff' },
        { condition: (d) => d.food >= 0.6, label: 'The Flavor Hunter' },
        { condition: (d) => d.adventure >= 0.6, label: 'The Adrenaline Chaser' },
        { condition: (d) => d.nature >= 0.6, label: 'The Nature Lover' },
        { condition: () => true, label: 'The Curious Wanderer' },
      ];

      const personality = PERSONALITY_LABELS.find(p => p.condition(normalizedDimensions))?.label || 'The Curious Wanderer';

      const fullResult = {
        dimensions: normalizedDimensions,
        personality,
        psychographic,
        travelSoul,
        source: 'swipe',
      };

      // Persist to localStorage
      try {
        localStorage.setItem('travelDNA', JSON.stringify(fullResult));
      } catch { /* ignore */ }

      haptic.success();
      setResult(fullResult);
    } else {
      setCurrentIndex(nextIndex);
    }
  }, [currentIndex, decisions]);

  const handleComplete = useCallback(() => {
    if (onComplete && result) {
      onComplete(result);
    }
  }, [onComplete, result]);

  if (result) {
    return (
      <Container
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <SwipeResultReveal
          dimensions={result.dimensions}
          personality={result.personality}
          psychographic={result.psychographic}
          travelSoul={result.travelSoul}
          onStartPlanning={handleComplete}
        />
      </Container>
    );
  }

  // Show top 3 cards stacked
  const visibleCards = SWIPE_CARDS.slice(currentIndex, currentIndex + 3).reverse();

  return (
    <Container
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <Header>
        <SkipButton onClick={onClose}>Skip</SkipButton>
        <ProgressText>{currentIndex + 1} / {SWIPE_CARDS.length}</ProgressText>
      </Header>

      <DeckArea>
        {visibleCards.map((card, i) => {
          const stackIndex = visibleCards.length - 1 - i;
          const isTop = stackIndex === 0;
          return (
            <SwipeCard
              key={card.id}
              card={card}
              onSwipe={handleSwipe}
              isTop={isTop}
              stackOffset={stackIndex}
            />
          );
        })}
      </DeckArea>

      <ProgressDots>
        {SWIPE_CARDS.map((_, i) => (
          <Dot
            key={i}
            $state={
              i < currentIndex
                ? (decisions[i]?.liked ? 'liked' : 'passed')
                : i === currentIndex ? 'current' : 'pending'
            }
          />
        ))}
      </ProgressDots>

      {currentIndex === 0 && (
        <Hint
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
        >
          Swipe right to like, left to pass
        </Hint>
      )}
    </Container>
  );
};

export default SwipeOnboarding;
